import { Injectable } from '@angular/core';
import {
  CourseChapter,
  Exercise,
  SchoolCourse,
  SchoolCourses,
  SchoolLessonWithExercises,
} from '@digilize/language-school/definitions';
import { findNestedObj } from '@digilize/shared/utils/helpers/src';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';

import { SchoolCoursesApiService } from './school-courses-api.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolCoursesService {
  private _currentCourseId = new BehaviorSubject(null);
  public currentCourseId$ = this._currentCourseId.asObservable().pipe(filter((pos) => !!pos));

  get currentCourseId() {
    return this._currentCourseId.value;
  }
  set currentCourseId(courseId: string) {
    this._currentCourseId.next(courseId);
  }
  private _currentChapterId = new BehaviorSubject(null);
  public currentChapterId$ = this._currentChapterId.asObservable().pipe(filter((pos) => !!pos));

  get currentChapterId() {
    return this._currentChapterId.value;
  }
  set currentChapterId(chapterId: string) {
    this._currentChapterId.next(chapterId);
  }
  private _courses = new BehaviorSubject<SchoolCourses>(null);
  public courses$ = this._courses.asObservable().pipe(filter((courses) => !!courses));
  public allAvailableChapters$ = this.courses$.pipe(
    map((courses) => {
      return courses.courses.reduce<CourseChapter[]>((prev, curr) => [...prev, ...curr.chapters], []);
    })
  );
  shouldOpenWelcomeInNextChapterMap = false;
  shouldAnimateLastFilledRoute = true;
  constructor(private schoolCoursesApiService: SchoolCoursesApiService) {}

  fetchAndUpdateCourses(): Observable<SchoolCourses> {
    return this.schoolCoursesApiService.getActiveCourses().pipe(
      tap((schoolCourses) => {
        this._courses.next(schoolCourses);
      })
    );
  }

  updateCourses(schoolCourses: SchoolCourses): void {
    this._courses.next(schoolCourses);
  }

  getCourse(courseId: string = this.currentCourseId): Observable<SchoolCourse> {
    return this.courses$.pipe(
      map((courses) => {
        return courses.courses.find((course) => courseId === course.id);
      })
    );
  }

  getChapter(chapterId?: string): Observable<CourseChapter> {
    chapterId = chapterId || this.currentChapterId;
    return this.allAvailableChapters$.pipe(
      map((availableChapters) => {
        return findNestedObj(availableChapters, 'id', chapterId) as CourseChapter;
      })
    );
  }

  getLesson(lessonId: string): Observable<SchoolLessonWithExercises> {
    return this.courses$.pipe(
      map((courses) => {
        const lesson = findNestedObj(courses, 'id', lessonId) as SchoolLessonWithExercises;
        return { ...lesson };
      })
    );
  }
  getExercise(exerciseId: string): Observable<Exercise> {
    return this.courses$.pipe(
      map((courses) => {
        return findNestedObj(courses, 'shop_quiz_id', exerciseId) as Exercise;
      })
    );
  }
}
